/*
 * resize-guard.js -- sdileny ochranny guard "nic nemenit v okne po zmene
 * velikosti viewportu" (1.27.13, zaverecna oprava dvojiteho tapu po
 * scrollu v Chrome iOS).
 *
 * MECHANISMUS VADY: Chrome iOS vrati pri prvnim tapu po scrollu spodni
 * listu -> vyska viewportu skoci (namereno 635 -> 874 px) -> prohlizec
 * prepocita pruniky VSECH IntersectionObserveru -> sekce/tlacitka pod
 * puvodnim spodnim okrajem dostanou `isIntersecting: true` PRESNE v
 * okamziku tapu -> modul prida tridu (`in`, `is-pulsing`), spusti
 * animaci nebo prepise text (count-up) -> WebKit to v hover fazi tapu
 * vezme jako zmenu obsahu a `click` zahodi. Uzivatel musi tapnout znovu.
 *
 * PROC SDILENY MODUL A NE `resize` listener v kazdem modulu: udalost
 * `resize` NENI casove svazana s dorucenim IO zaznamu (dve nezavisle
 * fronty) -- na stagingu se pulz a `.in` porad objevovaly v okne tapu
 * i s vlastnim `lastResizeAt` v modulu, protoze IO callback prisel DRIV
 * nez `resize`. Tenhle guard proto pri KAZDEM volani porovna
 * `innerWidth`/`innerHeight` s posledni znamou hodnotou (`note()`) --
 * zmenu velikosti tak zachyti i bez udalosti, v okamziku, kdy se na ni
 * modul ptá.
 *
 * POUZITI: `guard(fn)` provede `fn` hned, kdyz se viewport za poslednich
 * `GUARD_MS` nezmenil; jinak `fn` odlozi na konec okna (a pri dalsi
 * zmene znovu). Mutace se tedy NEZTRATI, jen se posune o ~300 ms -- pro
 * animaci sekce nebo pulz tlacitka nerozlisitelne, pro tap rozhodujici.
 * Vola se PRIMO V MISTE MUTACE (classList.add/remove, textContent,
 * img.loading), ne na urovni IO callbacku -- viz docblok `pulse.js`.
 */

/**
 * Ochranne okno po zmene velikosti viewportu (ms).
 *
 * 700, ne 300: brana `resizemut` na DEV ukazala, ze s 300 ms se mutace
 * jen posunou na ~310 ms po zmene -- porad uvnitr okna, ve kterem WebKit
 * po prvnim tapu po scrollu vyhodnocuje hover fazi (synteticky mousemove
 * + casovace/prechody, ktere sleduje). Cena: odhaleni sekce nebo pulz
 * zacne po navratu listy o 0,7 s pozdeji. Deje se to jen pri zmene
 * velikosti viewportu (lista, otoceni), ne pri beznem scrollu.
 */
const GUARD_MS = 700;

/** Posledni znama velikost viewportu. */
let lastWidth = 0;
let lastHeight = 0;

/** Cas posledni zjistene zmeny velikosti (`performance.now()`), 0 = zadna. */
let lastChangeAt = 0;

/**
 * @return {number} Monotonni cas v ms.
 */
function now() {
	return window.performance && window.performance.now ? window.performance.now() : Date.now();
}

/**
 * Porovna aktualni velikost viewportu s posledni znamou a pri zmene
 * zaznamena jeji cas. Vola se z `resize` i z kazdeho `guard()`.
 *
 * @return {void}
 */
function note() {
	const w = window.innerWidth;
	const h = window.innerHeight;

	if (lastWidth && lastHeight && (w !== lastWidth || h !== lastHeight)) {
		lastChangeAt = now();
	}

	lastWidth = w;
	lastHeight = h;
}

if (typeof window !== 'undefined') {
	lastWidth = window.innerWidth;
	lastHeight = window.innerHeight;
	window.addEventListener('resize', note, { passive: true });
}

/**
 * @return {boolean} true, kdyz se viewport zmenil za poslednich `GUARD_MS`.
 */
export function settling() {
	note();

	return !!lastChangeAt && now() - lastChangeAt < GUARD_MS;
}

/**
 * Provede `fn` mimo ochranne okno po zmene velikosti viewportu; uvnitr
 * okna ji odlozi na jeho konec (a pri dalsi zmene znovu, dokud se
 * viewport neustali).
 *
 * @param {function(): void} fn Mutace k provedeni.
 * @return {void}
 */
export function guard(fn) {
	note();

	const elapsed = lastChangeAt ? now() - lastChangeAt : Infinity;

	if (lastChangeAt && elapsed < GUARD_MS) {
		window.setTimeout(function () {
			guard(fn);
		}, GUARD_MS - elapsed + 10);

		return;
	}

	fn();
}
